/**
@format
@flow local-strict
*/
import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import Colors from '@ziynx/theme/colors';
import BannerNotification from '@ziynx/components/notifications';
import { Button } from '@ziynx/components/buttons';
import Input from '@ziynx/components/inputs';
import Link from '@ziynx/components/links';
import { withAppManager } from '@ziynx/components/utilities';
import { Body } from '@ziynx/components/typography';
import { ContentContainer } from '@ziynx/components/layout';
import { Form, FormRow } from '@ziynx/components/forms';
import Image from '@ziynx/components/images';
import ActionTypes from '@ziynx/action-types';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import logo from '../../assets/ZIYNX_LOGO_2.png';
import logoSmall from '../../assets/ZIYNX_SYMBOL_2 2x.png';

type Props = $ReadOnly<{|
  /**
   * Navigation stack
   */
  history: {
    push: (pathName: string) => void,
  },
  /**
   * Location represents where
   * the app is at right now
   */
  location: Object
|}>;

type DispatchType = ({
  type: string,
}) => void;

// Replace with card ??
const FormSectionWrapper = styled.div`
  display: table;
  padding: ${({ isMobileSmall }) => (isMobileSmall ? '0px 16px 48px' : '0px 40px 48px')};
  ${({ isMobile }) => !isMobile && 'margin: 0 auto;'}
`;

const StyledBannerWrapper = styled.div`
  z-index: 996;
  width: 100%;
  height: 0;
`;

const StyledBodyWrapper = styled.div`
  ${({ lastChild }) => !lastChild && ' padding-bottom: 40px;'};
  ${({ center }) => center && 'text-align: center;'}
`;

const FormSubTitle = styled(StyledBodyWrapper)`
  max-width: 480px;
  padding-bottom: ${({ isMobileSmall }) => (isMobileSmall ? '48px' : '56px')} ;
`;

const StyledButton = styled(Button)`
  margin-bottom: 16px;
`;

const ImageWrapper = styled.div`
  padding-bottom: 64px;
  padding-top: ${({ isMobile }) => (isMobile ? 32 : 48)}px;
`;

/**
 * Object to hold data for each types of inputs to
 * be used in Controller
 */
const inputsData = {
  data: [
    {
      id: '1',
      name: 'confirmCode',
      defaultValue: '',
      regExPattern: /^[0-9]{6}$/,
      errorMessage: 'Please enter 6-digit number',
    },
  ],
};

/**
 * Time for banner to setTimeout
 */
const bannerTimeOut = 5000;

/**
 * Object to store common error messages for banner notification
 * NOTE: will either delete some messages once figured out built in amplify error.message
 */
const bannerMessageData = {
  maxAttemptResendingError:
    'You have reached the maximum attempt to request a verification code. Please try again later.',
  maxAttemptConfirming:
    'Currently unable to confirm your account. Please try again later.',
  generalConfirmingError:
    'We ran into some trouble confirming your account. Please try again later.',
  generalResendingError:
    'An error occurred while re-sending your verification code. Please try again later.',
  successResendingCode:
    'An email with your verification code has been sent again. Please check your mailbox and spam folder.',
};

/**
 * Confirm code screen after user fills out create-account form
 */
const ConfirmCodeScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport } = props;
  const {
    handleSubmit, control, errors, setError, clearErrors, formState,
  } = useForm();

  const verificationService = useSelector(({ auth }) => auth[ActionTypes.auth.verifyAccount.GROUP]
  || {}, shallowEqual);

  const isMobileSmall = viewport === 'mobileSmall';
  const isMobile = viewport === 'mobileSmall' || viewport === 'mobile';

  const dispatch: DispatchType = useDispatch();

  const { location: { state } = {} } = props;
  const username = state?.email;
  const userPassword = state?.password;

  // Keep track if any inputs user modified
  const { dirtyFields } = formState;

  // When code is re-sent
  const [codeResent, setCodeResent] = useState(false);
  // When code re-sent or resetting password has error
  const [codeError, setCodeError] = useState(false);
  // state for banner error message
  const [bannerErrorMessage, setBannerErrorMessage] = useState('');
  // Shows if waiting for response from AWS
  const [isLoading, setIsLoading] = useState(false);
  // State to keep track if banner should be rendered
  const [shouldRenderBanner, setRenderBanner] = useState(false);

  const formSubtitle = 'We sent a verification code to your email. Please check your email and follow the instructions.';

  /**
   * Function to signal there's an error
   * and render banner
   */
  const setBannerError = (): void => {
    setCodeError(true);
    setRenderBanner(true);
  };

  /**
   * Function to sign in to newly signed up account, if successful
   */
  const signInIfConfirmed = async (): Promise<any> => {
    const auth = {
      auth: { username, password: userPassword, cognitoUser: undefined },
    };

    try {
      const user = await Auth.signIn({ username, password: userPassword, validationData: { platform: 'web' } });
      auth.auth.cognitoUser = user;
      dispatch({ type: ActionTypes.auth.checkIfLoggedIn.START });
    } catch (error) {
      setRenderBanner(false); // Reset banner state
      setCodeError(false);
      setBannerError();
      setBannerErrorMessage(error.message);
    }
  };

  /**
   * Function to re-send code to resend verification code
   */
  const resendCode = async (): Promise<any> => {
    try {
      await Auth.resendSignUp(username);

      setIsLoading(false);
      if (codeError) setCodeError(false); // In case of successful re-try when errored out
      setCodeResent(true);
      setRenderBanner(true);
    } catch (error) {
      setRenderBanner(false); // Reset banner state
      setCodeError(false);
      switch (error.code) {
      case 'InvalidParameterException':
        if (error.message === 'User is already confirmed.') {
          signInIfConfirmed();
          break;
        }
        setIsLoading(false);
        setBannerErrorMessage(error.message);
        break;
      case 'LimitExceededException':
      case 'TooManyRequestsException':
      case 'CodeDeliveryFailureException':
      case 'InternalErrorException':
      default:
        setIsLoading(false);
        setBannerErrorMessage(error.message);
        if (codeResent) setCodeResent(false);
        setBannerError();
        break;
      }
    }
  };

  /**
   * Function to handle when user clicks button to submit verification code
   */
  const onSubmit = async (data: Object): Promise<void> => {
    setIsLoading(true);

    const { confirmCode: code } = data || {};
    dispatch({
      type: ActionTypes.auth.verifyAccount.START,
      username,
      password: userPassword,
      code,
      invited: !!state?.invited,
    });
  };

  useEffect(() => {
    if (!verificationService.error) {
      return;
    }
    setRenderBanner(false); // Reset banner state
    setCodeError(false);
    switch (verificationService.error.code) {
    /**
       * Message: User cannot be confirmed. Current status is CONFIRMED
       * To handle for already-confirmed user -> patch through
       */
    case 'NotAuthorizedException':
      if (verificationService.message === 'User cannot be confirmed. Current status is CONFIRMED') {
        signInIfConfirmed();
        break;
      }
      setIsLoading(false);
      setBannerError();
      setBannerErrorMessage(verificationService.error.message);
      break;

    case 'CodeMismatchException':
      setIsLoading(false);
      setError('confirmCode', {
        type: 'manual',
        message: verificationService.error.message,
      });
      break;

    default:
      // Will handle for any internal errors and display that error's message
      // i.e InternalErrorException, TooManyFailedAttemptsException,
      setIsLoading(false);
      setBannerError();
      setBannerErrorMessage(verificationService.error.message);
      break;
    }
    dispatch({ type: ActionTypes.auth.clearErrors });
  }, [verificationService?.error]);

  /**
   * Function to render Inputs for Controller render prop
   * of useForm hook.
   * Using this function just in case more inputs are added
   * later on
   */
  const renderInputs = ({ onChange, onFocus, name }): React$Node => {
    let label: string = '';
    const variant = 'number'; // default type of inputs

    switch (name) {
    case 'confirmCode':
    default:
      label = 'Verification code';
      break;
    }

    /**
     * Set value of input
     */
    const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
      (event?.target: HTMLInputElement);
      onChange(event?.target?.value);
    };

    /**
     * Function to clear any errors of inputs
     * when on focus event, if any
     */
    const onInputFocus = (): any => {
      if (errors?.[name]) clearErrors(name);
      return onFocus;
    };

    const onWheel = (event: any): any => {
      event?.target?.blur();
    };

    return (
      <Input
        variant={variant}
        error={!!errors?.[name]}
        errorLabel={errors?.[name]?.message}
        inputName={name}
        label={label}
        onChange={onInputChange}
        onFocus={onInputFocus}
        onWheel={onWheel}
        required
      />
    );
  };

  /**
   * Function to map out FormRows
   * that holds the Controller of each types of inputs
   * based on inputsData
   */
  const renderInputsControllers = (): React$Node => inputsData.data.map((field) => (
    <FormRow key={field.id}>
      <Controller
        control={control}
        render={renderInputs}
        name={field.name}
        defaultValue={field.defaultValue}
        rules={{
          required: 'Please enter a value to continue',
          pattern: {
            value: field?.regExPattern,
            message: field?.errorMessage,
          },
        }}
      />
    </FormRow>
  ));

  /**
   * Function to check if dirtyFields length equals to inputsData length.
   * Returns true if not -> disable button
   */
  const isDisabled = (): boolean => {
    if (!inputsData?.data || !dirtyFields || isLoading) return true;
    return Object.keys(dirtyFields).length !== inputsData?.data?.length;
  };

  /**
   * Function to watch if an input has error
   * after user submit upon validation.
   * Errors object is {} by default - which means no error
   * Returns true if length of errors object != 0
   * and false if empty
   */
  const isError = (): boolean => !!Object.keys(errors).length;

  /**
   * To render ziynx logo as image
   */
  const renderLogo = (): React$Node => {
    if (isMobile) {
      return (
        <Image src={logoSmall} width="24px" height="24px" alt="company-logo" />
      );
    }
    return (
      <Image src={logo} width="114px" height="24px" alt="company-logo" />
    );
  };

  return (
    <>
      <Helmet>
        <title>Sign Up | ZIYNX</title>
      </Helmet>
      <ContentContainer offsetTop={0} isVertical>
        {shouldRenderBanner && (
          <StyledBannerWrapper>
            <BannerNotification animated timeout={bannerTimeOut} variant={codeError ? 'error' : 'success'}>
              {!codeError && codeResent && bannerMessageData.successResendingCode}
              {codeError && bannerErrorMessage}
            </BannerNotification>
          </StyledBannerWrapper>
        )}
        <FormSectionWrapper
          isMobileSmall={isMobileSmall}
          isMobile={isMobile}
          hasBanner={shouldRenderBanner}
        >
          <ImageWrapper isMobile={isMobile}>
            {renderLogo()}
          </ImageWrapper>
          <Form
            formID="confirm-code"
            title="Confirm your email"
            headingPaddingBottom="12px"
          >
            <FormSubTitle isMobileSmall={isMobileSmall}>
              <Body medium color={Colors.GrayDark}>
                {formSubtitle}
              </Body>
            </FormSubTitle>

            {renderInputsControllers()}

            <StyledBodyWrapper>
              <Body medium color={Colors.GrayDark}>
                Didn&apos;t get your code?&nbsp;
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link onClick={resendCode}>Resend code</Link>
              </Body>
            </StyledBodyWrapper>
            <StyledButton
              disabled={isDisabled() || isError()}
              isLoading={isLoading}
              type="submit"
              width="100%"
              variant="primary"
              form="confirm-code"
              onClick={handleSubmit(onSubmit)}
            >
              Submit
            </StyledButton>

            <StyledBodyWrapper center lastChild>
              <Link variant="navigation" activeOnlyWhenExact label="Back to sign up" to="/sign-up" />
            </StyledBodyWrapper>
          </Form>
        </FormSectionWrapper>
      </ContentContainer>
    </>
  );
};

ConfirmCodeScreen.defaultProps = {
  viewport: 'desktop',
};

export default (withAppManager(ConfirmCodeScreen): React$ComponentType<
  React$Config<ManagedProps<Props>, typeof ConfirmCodeScreen.defaultProps>
>);
